// DayCloseModal.tsx - Day Close 新流程
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useModal } from '../../contexts/ModalContext';
import { useConfirmDialog } from '../../contexts/ConfirmDialogContext';
import { useNotificationPermission } from '../../hooks/useNotificationPermission';
import { useDayCloseAPI } from '../../hooks/useDayCloseAPI';
import TomorrowPreviewStep from './TomorrowPreviewStep';
import { MeaningfulReviewStep } from './MeaningfulReviewStep';
import { RipplesVisualizationStep } from './RipplesVisualizationStep';
import { ContinuationItemsStep } from './ContinuationItemsStep';
import { ConfettiCelebration } from './ConfettiCelebration';
import JournalEntryStep from './journal/JournalEntryStep';
import { DayCloseStep, DayClose } from '../../types/dayClose';
import { X, CheckCircle } from 'lucide-react';
import { logger } from '../../utils/logger';

interface DayCloseModalProps {
    dayCloseId: string;
    isOpen: boolean;
    onClose: () => void;
}

// 步驟配置 - 移到組件外部避免每次渲染重新創建
const stepConfig: Record<DayCloseStep, { next: DayCloseStep | null; showSkip: boolean; title: string; buttonText: string }> = {
    review: { next: 'reflection', showSkip: false, title: 'Review', buttonText: 'Continue' },
    reflection: { next: 'journal', showSkip: false, title: 'Ripples', buttonText: 'Continue' },
    journal: { next: 'continuation', showSkip: true, title: 'Journal', buttonText: 'Continue' },
    continuation: { next: 'preview', showSkip: true, title: 'Continuation', buttonText: 'Continue' },
    preview: { next: 'complete', showSkip: false, title: 'Preview', buttonText: 'Complete' },
    complete: { next: null, showSkip: false, title: 'Complete', buttonText: 'Close' }
};

const DayCloseModal: React.FC<DayCloseModalProps> = ({
    dayCloseId,
    isOpen,
    onClose
}) => {
    const { currentUser } = useAuth();
    const { loadDayClose } = useDayCloseAPI();
    const { setIsDayCloseModalOpen, isDayCloseModalPaused } = useModal();
    const { showConfirm } = useConfirmDialog();
    const { fcmToken, isSupported, requestPermission } = useNotificationPermission();
    const [currentStep, setCurrentStep] = useState<DayCloseStep>('review');
    const [isVisible, setIsVisible] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [canContinue, setCanContinue] = useState(true);
    const [visitedSteps, setVisitedSteps] = useState<DayCloseStep[]>([]);
    const [dayCloseData, setDayCloseData] = useState<DayClose | null>(null);

    // 步驟處理函數的簡化版本
    const [stepHandlers, setStepHandlers] = useState<{
        handleSave?: () => Promise<void>;
        handleReflection?: () => Promise<void>;
        handleContinuation?: () => Promise<void>;
        handlePreview?: () => Promise<void>;
        handleComplete?: () => Promise<void>;
    }>({});

    // 載入完整的 Day Close 資料
    useEffect(() => {
        const loadFullDayCloseData = async () => {
            if (!dayCloseId) return;

            try {
                setIsLoading(true);
                const data = await loadDayClose(dayCloseId);
                setDayCloseData(data);

                // Always start from 'review' step
                setCurrentStep('review');
                setVisitedSteps(['review']);
            } catch (error) {
                logger.error('DayCloseModal', 'Failed to load Day Close data', error);
            } finally {
                setIsLoading(false);
            }
        };

        if (isOpen) {
            loadFullDayCloseData();
        }
    }, [dayCloseId, isOpen, loadDayClose]);


    // 處理滾動鎖定和動畫
    useEffect(() => {
        let timeoutId: NodeJS.Timeout;

        if (isOpen) {
            document.body.style.overflow = 'hidden';
            setIsVisible(true);
        } else {
            // 當關閉時，先保持可見以執行關閉動畫
            timeoutId = setTimeout(() => {
                setIsVisible(false);
            }, 300); // 與 CSS 過渡時間匹配
            document.body.style.overflow = '';
        }

        return () => {
            if (timeoutId) clearTimeout(timeoutId);
            document.body.style.overflow = '';
        };
    }, [isOpen]);

    useEffect(() => {
        setIsDayCloseModalOpen(isOpen);
        return () => {
            if (isOpen) {
                setIsDayCloseModalOpen(false);
            }
        };
    }, [isOpen, setIsDayCloseModalOpen]);

    // 簡化的關閉處理函數
    const handleClose = useCallback(async () => {
        setIsDayCloseModalOpen(false);
        onClose();

        // Day Close 完成後，詢問推播授權（如果尚未授權）
        if (currentStep === 'complete' && !fcmToken && isSupported) {
            const shouldEnable = await showConfirm({
                title: 'Stay on Track with Reminders',
                message: 'Get gentle reminders for your morning brief and day close',
                confirmText: 'Enable Notifications',
                cancelText: 'Not Now',
            });

            if (shouldEnable) {
                await requestPermission();
            }
        }
    }, [setIsDayCloseModalOpen, onClose, currentStep, fcmToken, isSupported, showConfirm, requestPermission]);

    // 點擊背景時關閉模態框
    const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
        if (e.target === e.currentTarget) {
            handleClose();
        }
    };

    // 簡化的步驟跳轉函數
    const moveToNextStep = useCallback(async () => {
        const nextStep = stepConfig[currentStep]?.next;
        if (nextStep) {
            setCurrentStep(nextStep as DayCloseStep);
            setVisitedSteps(prev =>
                prev.includes(nextStep as DayCloseStep)
                    ? prev
                    : [...prev, nextStep as DayCloseStep]
            );
        } else {
            handleClose();
        }
    }, [currentStep, handleClose]);

    // 處理步驟點擊
    const handleStepClick = useCallback((step: DayCloseStep) => {
        // 只允許跳轉到已訪問的步驟
        if (visitedSteps.includes(step)) {
            setCurrentStep(step);
        }
    }, [visitedSteps]);

    // 簡化的跳過函數
    const skipStep = useCallback(() => {
        moveToNextStep();
    }, [moveToNextStep]);

    // 簡化的繼續點擊處理函數
    const handleContinueClick = useCallback(async () => {
        try {
            setIsLoading(true);

            switch (currentStep) {
                case 'review':
                    if (stepHandlers.handleSave) {
                        await stepHandlers.handleSave();
                    } else {
                        moveToNextStep();
                    }
                    break;
                case 'reflection':
                    if (stepHandlers.handleReflection) {
                        await stepHandlers.handleReflection();
                    } else {
                        moveToNextStep();
                    }
                    break;
                case 'journal':
                    if (stepHandlers.handleReflection) {
                        await stepHandlers.handleReflection();
                    } else {
                        moveToNextStep();
                    }
                    break;
                case 'continuation':
                    if (stepHandlers.handleContinuation) {
                        await stepHandlers.handleContinuation();
                    } else {
                        moveToNextStep();
                    }
                    break;
                case 'preview':
                    if (stepHandlers.handlePreview) {
                        await stepHandlers.handlePreview();
                    } else if (stepHandlers.handleComplete) {
                        await stepHandlers.handleComplete();
                    } else {
                        moveToNextStep();
                    }
                    break;
                case 'complete':
                    handleClose();
                    break;
            }
        } catch (error) {
            logger.error('DayCloseModal', 'Error in step', error);
        } finally {
            setIsLoading(false);
        }
    }, [currentStep, stepHandlers, moveToNextStep, handleClose]);

    // 鍵盤快捷鍵支援
    useEffect(() => {
        if (!isOpen) return;

        const handleKeyDown = (e: KeyboardEvent) => {
            // ESC: 關閉 modal
            if (e.key === 'Escape' && !isLoading) {
                e.preventDefault();
                handleClose();
            }

            // Enter: 繼續到下一步（但不在 textarea 或 input 中）
            if (e.key === 'Enter' && !isLoading && canContinue) {
                const target = e.target as HTMLElement;
                if (target.tagName !== 'TEXTAREA' && target.tagName !== 'INPUT') {
                    e.preventDefault();
                    handleContinueClick();
                }
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [isOpen, isLoading, canContinue, handleClose, handleContinueClick]);



    // 早期返回必須在所有 Hook 之後
    if (!isOpen && !isVisible) return null;
    if (isDayCloseModalPaused) return null; // 暫停時不渲染，但保持所有狀態
    if (!currentUser) return null;

    const renderStepContent = () => {
        const stepProps = {
            dayCloseId,
            userId: currentUser.uid,
            onComplete: moveToNextStep,
            onSkip: skipStep,
            setLoading: setIsLoading,
            setCanContinue,
            setStepHandlers,
            dayCloseData,
        };

        switch (currentStep) {
            case 'review':
                return <MeaningfulReviewStep {...stepProps} />;
            case 'reflection':
                return <RipplesVisualizationStep {...stepProps} />;
            case 'journal':
                return <JournalEntryStep {...stepProps} />;
            case 'continuation':
                return <ContinuationItemsStep {...stepProps} />;
            case 'preview':
                return <TomorrowPreviewStep {...stepProps} />;
            case 'complete':
                return (
                    <>
                        <ConfettiCelebration trigger={true} />
                        <div className="p-6">
                            <h3 className="text-xl font-medium mb-4 text-center">Day Close Complete</h3>
                            <div className="text-center mb-6">
                                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4 animate-bounce">
                                    <CheckCircle className="w-10 h-10 text-green-600" />
                                </div>
                                <p className="text-gray-600">
                                    Great work today! Your contributions have been recorded.
                                </p>
                            </div>
                        </div>
                    </>
                );
        }
    };


    return (
        <div
            className={`fixed inset-0 bg-black bg-opacity-40 z-modal flex items-end md:items-center justify-center overflow-y-auto pt-2 md:pt-4 transition-opacity duration-300 ${isOpen ? 'opacity-100' : 'opacity-0'}`}
            onClick={handleBackdropClick}
        >
            <div
                className={`
                    bg-white w-full md:w-[640px] 
                    h-[90vh] md:max-h-[85vh] md:h-auto max-h-[calc(100vh-80px)]
                    rounded-t-2xl md:rounded-xl
                    overflow-hidden flex flex-col shadow-xl
                    transform transition-transform duration-300 ease-out
                    ${isOpen ? 'translate-y-0 md:translate-y-0 md:opacity-100' : 'translate-y-full md:translate-y-8 md:opacity-0'}
                    relative md:relative
                `}
                onClick={(e) => e.stopPropagation()}
            >
                {/* 拖動指示器 - 只在移動設備上顯示 */}
                <div className="md:hidden w-full flex justify-center pt-3 pb-1">
                    <div className="w-10 h-1 bg-gray-300 rounded-full"></div>
                </div>

                {/* Header */}
                <div className="px-4 md:px-6 py-4 border-b flex items-center justify-between flex-shrink-0">
                    <h2 className="text-xl font-medium">{stepConfig[currentStep]?.title}</h2>
                    <button
                        onClick={handleClose}
                        className="text-gray-500 hover:text-gray-700"
                        aria-label="Close"
                    >
                        <X className="h-6 w-6" />
                    </button>
                </div>

                {/* Progress indicator */}
                <div className="px-4 md:px-6 py-3 bg-gray-50 flex-shrink-0">
                    <div className="flex justify-between items-center">
                        {/* Review Step */}
                        <div className={`flex-1 ${currentStep === 'review' ? 'text-sky-600' : 'text-gray-400'}`}>
                            <div 
                                className={`flex items-center rounded-lg p-1 -m-1 ${
                                    visitedSteps.includes('review') 
                                        ? 'cursor-pointer hover:bg-sky-50 hover:text-sky-700 transition-all duration-200' 
                                        : ''
                                }`}
                                onClick={() => handleStepClick('review')}
                            >
                                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium ${currentStep === 'review' ? 'bg-sky-600 text-white' :
                                    ['reflection', 'journal', 'continuation', 'preview', 'complete'].includes(currentStep) ? 'bg-sky-100 text-sky-600' : 'bg-gray-200'
                                    }`}>
                                    {['reflection', 'journal', 'continuation', 'preview', 'complete'].includes(currentStep) ? '✓' : '1'}
                                </div>
                                <span className="ml-2 text-sm font-medium">Review</span>
                            </div>
                        </div>
                        <div className="w-8 flex-1 h-1 bg-gray-200 self-center mx-2">
                            <div className={`h-1 bg-sky-600 transition-all duration-300 ${['reflection', 'journal', 'continuation', 'preview', 'complete'].includes(currentStep) ? 'w-full' : 'w-0'}`}></div>
                        </div>
                        {/* Reflection Step */}
                        <div className={`flex-1 ${currentStep === 'reflection' ? 'text-sky-600' : 'text-gray-400'}`}>
                            <div 
                                className={`flex items-center rounded-lg p-1 -m-1 ${
                                    visitedSteps.includes('reflection') 
                                        ? 'cursor-pointer hover:bg-sky-50 hover:text-sky-700 transition-all duration-200' 
                                        : ''
                                }`}
                                onClick={() => handleStepClick('reflection')}
                            >
                                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium ${currentStep === 'reflection' ? 'bg-sky-600 text-white' :
                                    ['journal', 'continuation', 'preview', 'complete'].includes(currentStep) ? 'bg-sky-100 text-sky-600' : 'bg-gray-200'
                                    }`}>
                                    {['journal', 'continuation', 'preview', 'complete'].includes(currentStep) ? '✓' : '2'}
                                </div>
                                <span className="ml-2 text-sm font-medium">Reflect</span>
                            </div>
                        </div>
                        <div className="w-8 flex-1 h-1 bg-gray-200 self-center mx-2">
                            <div className={`h-1 bg-sky-600 transition-all duration-300 ${['preview', 'complete'].includes(currentStep) ? 'w-full' : 'w-0'}`}></div>
                        </div>
                        {/* Preview Step */}
                        <div className={`flex-1 ${currentStep === 'preview' ? 'text-sky-600' : 'text-gray-400'}`}>
                            <div 
                                className={`flex items-center rounded-lg p-1 -m-1 ${
                                    visitedSteps.includes('preview') 
                                        ? 'cursor-pointer hover:bg-sky-50 hover:text-sky-700 transition-all duration-200' 
                                        : ''
                                }`}
                                onClick={() => handleStepClick('preview')}
                            >
                                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium ${currentStep === 'preview' ? 'bg-sky-600 text-white' :
                                    currentStep === 'complete' ? 'bg-sky-100 text-sky-600' : 'bg-gray-200'
                                    }`}>
                                    {currentStep === 'complete' ? '✓' : '3'}
                                </div>
                                <span className="ml-2 text-sm font-medium">Preview</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Step content - 可滾動或置中 */}
                <div className={`flex-1 ${currentStep === 'complete' ? 'flex items-center justify-center' : 'overflow-y-auto'}`}>
                    <div className="animate-slide-in">
                        {renderStepContent()}
                    </div>
                </div>

                {/* Footer - 固定按鈕欄 */}
                <div className="px-4 md:px-6 py-4 border-t bg-white flex-shrink-0">
                    <div className="flex justify-between gap-3">
                        {stepConfig[currentStep]?.showSkip ? (
                            <button
                                onClick={skipStep}
                                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                                disabled={isLoading}
                            >
                                Skip
                            </button>
                        ) : (
                            <div></div>
                        )}

                        <button
                            onClick={handleContinueClick}
                            className="px-6 py-2 bg-sky-600 text-white rounded-lg shadow-sm hover:bg-sky-700 disabled:bg-sky-300 transition-colors flex items-center"
                            disabled={isLoading || !canContinue}
                        >
                            {isLoading ? (
                                <>
                                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    Processing...
                                </>
                            ) : stepConfig[currentStep]?.buttonText}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DayCloseModal;